import './HistoryTab.css';
import GameStats from './GameStats.js';
import Game from './Game.js';
import { difficultyOf } from './difficulty.js';
import { useState } from 'react';

const MAX_ENTRIES = 100;

// Display text only - the status keys these map from stay as they are
const STATUS_LABELS = {
  clear: 'Cleared',
  complete: 'Complete',
  unsolved: 'Incomplete',
};

function modeName(numbers) {
  return difficultyOf(numbers)?.label ?? `${numbers.length}×${numbers[0].length}`;
}

function timeAgo(timestamp) {
  if (!timestamp) return '';

  const minutes = Math.floor((Date.now() - timestamp) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const BoardThumbnail = ({ numbers }) => (
  <div className="history-thumb" style={{ '--cols': numbers[0].length }}>
    {numbers.flat().map((num, index) => (
      <span
        key={index}
        className="history-thumb-cell"
        style={{ opacity: 0.2 + (num / 9) * 0.8 }}
      />
    ))}
  </div>
);

// Always visible - a hover-only affordance would never show up on touch
const ReplayIcon = () => (
  <svg className="history-replay" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
    <path d="M21 3v5h-5" />
  </svg>
);

const HistoryItem = ({ entry, onReplay }) => {
  let game;
  try {
    game = Game.decode(entry.gameCode);
  } catch (error) {
    console.error('Could not decode game from history:', entry.gameCode, error);
    return null;
  }

  const numbers = game.getNumbers();

  return (
    <li>
      <button className="history-item" onClick={() => onReplay(entry.gameCode)}>
        <BoardThumbnail numbers={numbers} />
        <div className="history-item-body">
          <span className="history-status">
            <span className={`history-dot ${entry.status}`}></span>
            {STATUS_LABELS[entry.status]}
          </span>
          <span className="history-meta">
            {modeName(numbers)} · {timeAgo(entry.lastActivity)}
          </span>
        </div>
        <ReplayIcon />
      </button>
    </li>
  );
};

function HistoryTab({ onReplay = () => {} }) {

  const [filter, setFilter] = useState('all');
  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const entries = GameStats.getHistoryEntries();
  const filtered = filter === 'all' ? entries : entries.filter(entry => entry.status === filter);
  const visible = filtered.slice(0, MAX_ENTRIES);

  return (
    <>
      <div className="difficulty-selector invisible-hud">
        <select value={filter} onChange={handleFilterChange} aria-label="Filter games">
          <option value="all">🗃️ All games</option>
          <option value="clear">🏆 Cleared</option>
          <option value="complete">✅ Complete</option>
          <option value="unsolved">⏳ Incomplete</option>
        </select>
      </div>

      <section className="title-section history-header">
        <h1>History</h1>
        <small>{entries.length === 0 ? 'Nothing here yet' : 'Tap a board to play it again'}</small>
      </section>

      {visible.length === 0 ? (
        <p className="history-empty">
          {entries.length === 0 ? 'Play a game and it will show up here.' : 'No games match this filter.'}
        </p>
      ) : (
        <ul className="history-list">
          {visible.map(entry => (
            <HistoryItem key={entry.gameCode} entry={entry} onReplay={onReplay} />
          ))}
          {filtered.length > visible.length && (
            <li className="history-truncated">Showing the {MAX_ENTRIES} most recent</li>
          )}
        </ul>
      )}
    </>
  );
}

export default HistoryTab;
